<script setup>
import ChatCustomerTaskAction from "@frontJs/components/actions/projectManagement/tasks/ChatCustomerTaskAction.vue";
import ConvertCustomerTaskIdeaAction from "@frontJs/components/actions/projectManagement/tasks/ConvertCustomerTaskIdeaAction.vue";
import ToggleCustomerTaskDoneAtAction from "@frontJs/components/actions/projectManagement/tasks/ToggleCustomerTaskDoneAtAction.vue";
import ToggleCustomerTaskValidatedAction from "@frontJs/components/actions/projectManagement/tasks/ToggleCustomerTaskValidatedAction.vue";
import {
  ArrowPathIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import ActionButton from "@js/components/ActionButton.vue";
import { useRoute } from "@js/utils/route";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { row, callback, toValidate, idea, done } = defineProps({
  row: {
    type: Object,
    required: true,
  },
  callback: {
    type: Function,
    required: true,
  },
  toValidate: {
    type: Boolean,
    required: false,
  },
  idea: {
    type: Boolean,
    required: false,
  },
  done: {
    type: Boolean,
    required: true,
  },
});

const { t } = useI18n();

const { route } = useRoute();

function saveActionCallback() {
  toast.success(t("toast.successfullySaved"), { position: "top-right" });
  callback();
}
</script>

<template>
  <div class="flex h-full items-center gap-2 p-4">
    <template v-if="idea && row.original.isIdea">
      <ConvertCustomerTaskIdeaAction
        :tenant-task="row.original"
        :callback="saveActionCallback"
      >
        <template #button="{ action }">
          <ActionButton
            size="small"
            type="secondary"
            class="size-8"
            :callback="() => action()"
            :title="t('button.convertToTask')"
            :icon="ArrowPathIcon"
          />
        </template>
      </ConvertCustomerTaskIdeaAction>
    </template>

    <template v-else>
      <ToggleCustomerTaskDoneAtAction
        v-if="!toValidate"
        :tenant-task="row.original"
        :callback="
          () => {
            toast.success(t('toast.successfullySaved'), {
              position: 'top-right',
            });

            if (callback) {
              callback();
            }
          }
        "
      >
        <template #button="{ action, loading }">
          <ActionButton
            size="small"
            class="size-8"
            :title="
              row.original.doneAt === null
                ? t('button.validateCustomerTask')
                : t('button.invalidateCustomerTask')
            "
            type="secondary"
            :callback="() => action()"
            :icon="row.original.doneAt === null ? CheckIcon : XMarkIcon"
            :loading="loading"
          />
        </template>
      </ToggleCustomerTaskDoneAtAction>

      <template v-else>
        <ToggleCustomerTaskValidatedAction
          :tenant-task="row.original"
          :callback="
            () => {
              toast.success(t('toast.successfullySaved'), {
                position: 'top-right',
              });

              if (callback) {
                callback();
              }
            }
          "
        >
          <template #button="{ action, loading }">
            <ActionButton
              size="small"
              class="size-8"
              :title="t('button.validateCustomerTask')"
              type="secondary"
              :callback="() => action()"
              :icon="CheckIcon"
              :loading="loading"
            />
          </template>
        </ToggleCustomerTaskValidatedAction>

        <ToggleCustomerTaskDoneAtAction
          :tenant-task="row.original"
          :callback="
            () => {
              toast.success(t('toast.successfullySaved'), {
                position: 'top-right',
              });

              if (callback) {
                callback();
              }
            }
          "
        >
          <template #button="{ action, loading }">
            <ActionButton
              size="small"
              class="size-8"
              :title="t('button.invalidateCustomerTask')"
              type="secondary"
              :callback="() => action()"
              :icon="XMarkIcon"
              :loading="loading"
            />
          </template>
        </ToggleCustomerTaskDoneAtAction>
      </template>
    </template>

    <ActionButton
      size="small"
      class="size-8"
      :title="t('button.edit')"
      :href="
        idea
          ? route('front.projectManagement.task.customerTaskIdea', {
              id: row.original.id,
            })
          : route('front.projectManagement.task.customerTask', {
              id: row.original.id,
            })
      "
      :icon="PencilIcon"
    />

    <ChatCustomerTaskAction :tenant-task="row.original" :callback="callback">
      <template #button="{ setIsOpen }">
        <ActionButton
          size="small"
          type="primary"
          class="size-8"
          :callback="() => setIsOpen(true)"
          :title="t('button.chat')"
          :icon="ChatBubbleBottomCenterTextIcon"
          :ping="row.original.hasNewCustomerAnswer && !done"
        />
      </template>
    </ChatCustomerTaskAction>
  </div>
</template>
